import { useTranslations } from 'next-intl';
import { FormProvider, useForm } from 'react-hook-form';
import { useFormState } from 'react-dom';
import { v4 as uuid } from 'uuid';
import {
  Drawer,
  DealerLocatorHeader,
  DrawerFooter,
  DealerLocatorSearchResults,
  DrawerBody,
  DealerLocatorSearchResult,
  Conditional
} from '@cat-ecom/pcc-components';
import { CatIconWarningTriangle } from 'blocks-nextjs/blocks-react/components/Icons/WarningTriangle';
import { CatButton } from 'blocks-nextjs/blocks-react/components/Button';
import styles from './DealerOtherBranchesDrawer.module.scss';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  generateContactUrl,
  getFilteredAndOrderedDealerStores
} from 'components/dealer-locator/dealer-locator-content/utils';
import { useEsiteCtx } from 'services/esite/context';
import { DealerOtherBranchesDrawerProps } from './types';
import { DealerOtherStoreAvailability } from './dealer-other-store-availability';
import { SelectedStore } from 'services/dealer-locator/types';
import {
  SELECT_STORE_INITIAL_STATE,
  SUCCESS,
  ERROR
} from 'services/dealer-locator/constants';
import { CHECK_OTHER_LOCATION_FORM_ID } from '@cat-ecom/pcc-components/src/components/dealer-locator/constants';
import { CatLoadingIndicator } from 'components/blocks-components';
import { useProductDetailPageContext } from 'app/[lang]/[esite]/(core)/product/[product]/context/product-detail';
import { setSessionStorage } from '@cat-ecom/common/utils/storageUtils';
import { dealerBranchSelectStore } from 'services/dealer-branches/action';
import { PART_NUM_QUANTITY } from './constants';
import { DealerBranchSelectStoreResponse } from 'services/dealer-branches/types';
import { StoreDetailsServer } from 'components/dealer-locator/dealer-locator-content/dealer-locator-details-server';
import ErrorMessageBanner from 'components/error-inline-banner/error-inline-banner';
import { useLocationCtx } from 'services/location/context';
import { PCC_DEALER_LOCATION_FLAG } from 'constants/featureFlag';

export const DealerOtherBranchesDrawer = ({
  drawer,
  dealerBranchInfo
}: DealerOtherBranchesDrawerProps) => {
  const storeFormMethods = useForm();
  const t = useTranslations();
  const [selectStoresError, setSelectStoresError] = useState(false);
  const { filteredAndOrderedVisibleDealerStores } =
    getFilteredAndOrderedDealerStores(dealerBranchInfo?.locations?.slice(0, 6));
  const defaultStoreId =
    filteredAndOrderedVisibleDealerStores[0]?.id.toString();
  const { catalogId, langId } = useEsiteCtx();
  const returnContactUrl = generateContactUrl(catalogId, langId);
  const getOtherStoreAvailability = (store) => (
    <DealerOtherStoreAvailability
      store={store}
      defaultStoreId={defaultStoreId}
      closeAvailabilityDrawer={drawer?.closeDrawer}
    />
  );
  const showStoresList = !!filteredAndOrderedVisibleDealerStores;
  const SELECTED_STORE_ID = 'selectedStoreId';
  const setFirstStoreAsDefault = useCallback(() => {
    storeFormMethods.setValue(SELECTED_STORE_ID, defaultStoreId);
  }, [storeFormMethods, defaultStoreId]);
  const prevSelectedStoreUniqueIdRef = useRef(null);
  const selectedStoreUniqueId = storeFormMethods.watch(SELECTED_STORE_ID);
  const { quantity, partsList } = useProductDetailPageContext();
  const partNumber = partsList[0]?.partNumber;
  const isDefaultStoreSelected = defaultStoreId === selectedStoreUniqueId;
  const { featureFlags } = useLocationCtx();
  const isDealerLocationFlag = featureFlags?.[PCC_DEALER_LOCATION_FLAG];

  const [selectStoreState, selectStoreAction] = useFormState<
    DealerBranchSelectStoreResponse,
    SelectedStore
  >(dealerBranchSelectStore, SELECT_STORE_INITIAL_STATE);

  const formId = `${uuid()}-${CHECK_OTHER_LOCATION_FORM_ID}`;
  const selectedId = storeFormMethods.getValues(SELECTED_STORE_ID);
  const selectedStore = filteredAndOrderedVisibleDealerStores?.find(
    ({ id }) => id.toString() === selectedId
  );

  const [isButtonClicked, setIsButtonClicked] = useState(false);
  const selectStoreWithParams = () => {
    selectStoreAction.bind(null, selectedStore)();
  };
  useEffect(() => {
    if (selectStoreState?.message === SUCCESS) {
      selectStoreAction(null);
      setIsButtonClicked(false);
      setSessionStorage(
        PART_NUM_QUANTITY,
        `${partNumber?.toLowerCase()}_${quantity}`
      );
      drawer.handleClose();
      window.location.reload();
    }
    if (selectStoreState?.message === ERROR) {
      setSelectStoresError(true);
      setIsButtonClicked(false);
    }
  }, [
    drawer,
    partNumber,
    quantity,
    selectStoreAction,
    selectStoreState?.message
  ]);
  // This useEffect is to reset the error state to false when user tries to select a Different Store Location
  useEffect(() => {
    if (prevSelectedStoreUniqueIdRef.current !== selectedStoreUniqueId) {
      // Only reset if the selectedStoreUniqueId has actually changed
      if (selectStoresError) {
        setSelectStoresError(false);
        selectStoreAction(null);
      }
      // Update the ref to the current selectedStoreUniqueId
      prevSelectedStoreUniqueIdRef.current = selectedStoreUniqueId;
    }
  }, [selectedStoreUniqueId, selectStoresError, selectStoreAction]);
  //   set the first store as default if the user never changed default radio selection and the first store in the list changes due to numDisplayed increasing
  useEffect(() => {
    if (showStoresList && drawer.isDrawerOpen) {
      setFirstStoreAsDefault();
    }
  }, [showStoresList, drawer.isDrawerOpen, setFirstStoreAsDefault]);
  return (
    <FormProvider {...storeFormMethods}>
      <form
        action={selectStoreWithParams}
        id={formId}
        onSubmit={() => setIsButtonClicked(true)}
      >
        <Drawer className="color-black" {...drawer.drawerProps}>
          <DealerLocatorHeader
            title={t('AVAILABLE_AT_OTHER_LOCATIONS')}
          ></DealerLocatorHeader>
          <Conditional test={selectStoresError}>
            <div className="mb-3 pb-3">
              <ErrorMessageBanner
                heading={t('SORRY_ERROR_OCCURRED')}
                data-testid="select-store-error"
              >
                {t('UNKNOWN_ERROR_MESSAGE')}
              </ErrorMessageBanner>
            </div>
          </Conditional>
          <DrawerBody className={`${styles['drawer_body_class']}`}>
            <Conditional test={showStoresList}>
              <DealerLocatorSearchResults
                visibleStores={filteredAndOrderedVisibleDealerStores}
              >
                {({ store }) => {
                  const { id } = store;
                  return (
                    <DealerLocatorSearchResult
                      key={id}
                      unitOfMeasure={dealerBranchInfo?.unitOfMeasure || 'mi'}
                      closestLabel={t('AVAILABLE_AT_CURRENT_STORE')}
                      store={store}
                      showAvailabilityBTNAndMsg={true}
                      availabilityBtnAndMsg={getOtherStoreAvailability(store)}
                      storeDetailsLabel={t('INV_STORE_DETAILS')}
                    >
                      <StoreDetailsServer
                        store={store}
                        returnContactUrl={returnContactUrl}
                        isAvailByBranch={true}
                        isDealerLocationFlag={isDealerLocationFlag}
                      />
                    </DealerLocatorSearchResult>
                  );
                }}
              </DealerLocatorSearchResults>
            </Conditional>
          </DrawerBody>
          <DrawerFooter>
            <span className="d-flex ">
              <CatIconWarningTriangle
                className={`${styles['drawer_warning_icon']}`}
                size="lg"
                data-testid="warning-icon"
              />

              <p className="u-fs--12 u-color--ExtraDarkGray ps-2">
                {t('AVAILABLE_DISCLAIMER_STORE_LOCATION')}
              </p>
            </span>
            <CatButton
              variant="primary"
              type="submit"
              form={formId}
              disabled={
                isButtonClicked || isDefaultStoreSelected || selectStoresError
              }
            >
              {t('CAT_SELECT_STORE')}
              {isButtonClicked && <CatLoadingIndicator small slot="after" />}
            </CatButton>
            <span className="mx-3">
              <CatButton
                disabled={isButtonClicked}
                onClick={drawer.handleClose}
              >
                {t('CANCEL')}
              </CatButton>
            </span>
          </DrawerFooter>
        </Drawer>
      </form>
    </FormProvider>
  );
};

export default DealerOtherBranchesDrawer;
